import { Link, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useAcessoDaTela } from '@/lib/acesso-store';
import { chaveDaRota } from '@/lib/acesso-rotas';
import { isModuleRoute, isConfiguracoesNova } from './admin-layout-helpers';
import { SECAO_IDS, SECOES_CONFIG } from './configuracoes/catalogo';

/**
 * Layout das telas admin com sub-navegação horizontal em DOIS NÍVEIS (A5.3).
 *
 * URL: /admin/*
 *
 * Antes (A4): 18 abas planas em linha única → densidade extrema, "feio"
 * (feedback Matheus). Confunde + escondia módulos novos (A1/A2/A3/A4).
 *
 * Depois (A5.3 Pente Fino): grupos no nível 1, sub-itens no nível 2.
 *
 * 5 grupos:
 *   - Pessoas (Usuários, Perfis, Departamentos, Permissões)
 *   - Cadastros (Empresas Contratantes, Responsáveis, Contratos,
 *     Contratos Master, Catálogos, Catálogos R2, Tags) — Clientes saiu p/ não
 *     duplicar a sidebar (B2 Lote 2 #5)
 *   - Operação (Projeto-Tipos A4, Workflows, Tipos de Atividade,
 *     Questionários, Automações) — Projetos saiu p/ não duplicar a sidebar (#6)
 *   - Integrações (Omie Sync, Omie Audit, Ativar Write, Produtos Universal,
 *     Produtos Conciliar, Financeiro Dashboard A2)
 *   - Sistema (Visual Builder A3, Configurações, Feature Flags)
 *
 * Acesso (PERMISSOES-ACESSO): admin passa por tudo; quem não é admin passa se
 * a rota tiver uma CHAVE mapeada (`@/lib/acesso-rotas`) e a pessoa tiver essa
 * chave (regra no banco, editável em Permissões). Rota sem chave = admin-only.
 *
 * NÃO REVERTER: Manter agrupamento por domínio. NÃO voltar pra lista plana.
 *
 * --- B-FINAL.6.2 (BUG-002/003/006) ---
 * Módulos operacionais (Financeiro, Projetos, Clientes-V2) são páginas
 * com sua própria identidade — NÃO devem ser percebidos como "sub-item
 * de Configurações". Quando pathname matchea um MODULE_ROUTE_PREFIXES,
 * AdminLayout pula o "Configurações" header + group nav e renderiza só
 * `<AppShell><Outlet /></AppShell>`. A página filha renderiza próprio header.
 *
 * Rotas de config restantes (/admin/usuarios, /admin/perfis, /admin/configuracoes,
 * etc) continuam com o wrap completo do AdminLayout.
 */

// B2 Lote 2: `ADMIN_GROUPS` + `resolveActiveGroup` + tipos migraram pra
// `./admin-layout-helpers.ts` (testáveis sem render; regra react-refresh).

// BUG-002/003/006: helpers `MODULE_ROUTE_PREFIXES` + `isModuleRoute`
// extraídos pra `./admin-layout-helpers.ts` (rega react-refresh + facilita
// testes unitários sem render).

/**
 * G-3 — a barra "voltar pra Configurações".
 *
 * A tela nova mandava a pessoa pro destino e ali a navegação virava a antiga,
 * **sem caminho de volta**: o breadcrumb "CONFIGURAÇÕES · INTEGRAÇÕES" não era
 * link, e o único elemento clicável chamado "Configurações" era o item da
 * sidebar. Voltava-se só pelo botão do navegador.
 *
 * Resolvido num lugar só: os itens do catálogo carimbam `?de=<secao>` no
 * destino, e esta barra aparece em QUALQUER tela que chegue com esse parâmetro.
 * A alternativa era editar as 25 telas de destino, uma a uma — e cada tela nova
 * nasceria sem o caminho de volta de novo.
 */
function VoltarParaConfiguracoes() {
  const [params] = useSearchParams();
  const location = useLocation();
  const de = params.get('de');
  if (!de || !SECAO_IDS.includes(de)) return null;
  // Cinto e suspensório: mesmo que um `?de=` chegue aqui por outro caminho
  // (link antigo, favorito), a barra não aparece DENTRO da casca — lá o menu de
  // seções já é o caminho de volta, e o botão vira ruído.
  if (isConfiguracoesNova(location.pathname, SECAO_IDS)) return null;
  const secao = SECOES_CONFIG.find((s) => s.id === de);
  return (
    <Link
      to={`/admin/configuracoes/${de}`}
      className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink-1"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      <span>Voltar para Configurações · {secao?.nome ?? de}</span>
    </Link>
  );
}

export function AdminLayout() {
  const { user } = useAuth();
  const location = useLocation();
  // G-2: a Configurações nova entra no mesmo modo das telas de módulo — ela traz
  // o próprio menu de seções, e a barra de abas antiga por cima seria a segunda
  // navegação do mesmo assunto na mesma tela.
  const moduleMode = useMemo(
    () =>
      isModuleRoute(location.pathname) || isConfiguracoesNova(location.pathname, SECAO_IDS),
    [location.pathname],
  );

  // PERMISSOES-ACESSO (P3): o porteiro deixou de ser lista de rotas no código.
  //
  // Antes: `if (!isAdmin && !acessoColabComercial && !acessoOrcamentista)`, com
  // duas listas de prefixos ao lado (`COMERCIAL_COLAB_ROUTE_PREFIXES` e
  // `COMERCIAL_ORCAMENTISTA_ROUTE_PREFIXES`) — liberar uma pessoa exigia deploy.
  //
  // Agora: a URL vira uma CHAVE (`acesso-rotas.ts`, dicionário puro) e quem
  // pode abrir a chave está no banco, editável em Configurações › Pessoas ›
  // Permissões. Rota sem chave mapeada continua ADMIN-ONLY — nada abre por
  // acidente.
  const chaveDaTela = useMemo(() => chaveDaRota(location.pathname), [location.pathname]);
  const { permitido, carregando } = useAcessoDaTela(chaveDaTela);
  const liberado = user?.isAdmin === true || permitido;

  // Enquanto os acessos chegam, NUNCA mostrar "Acesso restrito": piscar
  // bloqueio na cara de quem TEM acesso é regressão percebida.
  if (!liberado && carregando) {
    return (
      <AppShell>
        <PageContainer>
          <div className="text-center" data-testid="admin-acesso-carregando">
            <p className="text-ink-2 dark:text-ink-dark-2">Carregando…</p>
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  if (!liberado) {
    return (
      <AppShell>
        {/* LAYOUT-CANONICO: o `px-4 md:px-8 py-10 max-w-3xl mx-auto` próprio
            virou a régua única; o `text-center` é layout interno e ficou no div
            de dentro. */}
        <PageContainer>
          <div className="text-center">
            <h1 className="font-display text-4xl text-ink-1 dark:text-ink-dark-1 mb-4">
              Acesso restrito
            </h1>
            <p className="text-ink-2 dark:text-ink-dark-2 mb-8">
              Você ainda não tem acesso a esta tela. Peça a um administrador para liberar em
              Configurações › Pessoas › Permissões.
            </p>
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  // BUG-002/003/006: rotas de módulo (Financeiro, Projetos, Clientes V2)
  // renderizam SEM "Configurações" header + group nav. A página filha
  // mantém sua identidade visual própria. Quem NÃO é admin e entrou por uma
  // chave de acesso também renderiza sem a casca — o redirect /admin/comercial
  // não é rota de módulo e sem isso ele veria a casca de Configurações (com as
  // abas de admin) por um instante.
  if (moduleMode || (!user?.isAdmin && chaveDaTela !== null)) {
    return (
      <AppShell>
        <div data-testid="admin-module-mode" className="w-full min-w-0">
          <VoltarParaConfiguracoes />
          <Outlet />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {/* LAYOUT-CANONICO: a casca de Configurações usa a MESMA régua das telas
          de módulo. Como a nav de dois níveis mora aqui, é este PageContainer
          que serve nav + tela filha — por isso as telas de Configurações não
          têm (nem podem ter) wrapper próprio. */}
      <PageContainer>
        {/* FASE-C: o h1 gigante "Configurações" saiu daqui. Ele se repetia em ~15
            telas e roubava o título específico de cada uma (dor 9 do diagnóstico).
            Agora CADA tela filha renderiza seu próprio <PageHeader> com
            crumb "Configurações · <Grupo>" + h1 próprio. A navegação em dois
            níveis abaixo continua idêntica (itens, ordem e rótulos intocados). */}

        {/* ROTAS-ORFAS (06/08/2026) — a barra de abas antiga CAIU.
            Eram 27 links em dois níveis, e ela era a única casa de rotas que
            ninguém achava de outro jeito. As sete decisões do Matheus deram
            destino a cada uma (área Terceiros, Workflows admin-only, Tipos de
            Projeto na aba Projetos, Automações guardada nos catálogos,
            Responsáveis dentro da ficha do cliente), e só então ela desceu.

            A prova de que nada ficou inalcançável é mecânica, rota por rota:
            `__tests__/barra-antiga-nada-inalcancavel.test.ts`.

            Quem navega hoje: a sidebar para os módulos, a Configurações para
            o que se configura, e a busca do topo (Ctrl+K) para achar pelo
            nome. Duas navegações para o mesmo assunto era o problema. */}

        {/* Outlet renderiza a página específica */}
        <VoltarParaConfiguracoes />
        <Outlet />
      </PageContainer>
    </AppShell>
  );
}
